import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Briefcase, FileText, Settings, LogOut, Loader2 } from "lucide-react";
import { getToken, clearToken, fetchMe, refreshSession, logout } from "@/lib/mastroAuth";
import { MastroBrand } from "@/components/MastroBrand";
import { DASHBOARD } from "@/constants/testIds";

const NAV = [
  { to: "/app", end: true, label: "Home", icon: HomeIcon, tid: DASHBOARD.navHome },
  { to: "/app/lavori", label: "Lavori", icon: Briefcase, tid: DASHBOARD.navLavori },
  { to: "/app/preventivi", label: "Preventivi", icon: FileText, tid: DASHBOARD.navPreventivi },
  { to: "/app/impostazioni", label: "Impostazioni", icon: Settings, tid: DASHBOARD.navImpostazioni },
];

const linkClasses = ({ isActive }) =>
  [
    "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-teal-50 text-teal-700"
      : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950",
  ].join(" ");

export default function DashboardLayout() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [workspace, setWorkspace] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      let token = getToken();
      // Whether the persistent-session cookie was already consumed to obtain
      // `token` in this mount — used to block a second refresh attempt and
      // guarantee at-most-one /auth/refresh per mount (no loops).
      let refreshedThisMount = false;

      // No in-tab token: try to silently restore a persistent session from the
      // httpOnly refresh cookie ("Rimani collegato"). Unchanged behavior.
      if (!token) {
        try {
          const data = await refreshSession();
          token = data?.access_token || null;
          refreshedThisMount = true;
        } catch (_) {
          token = null;
        }
        if (!token) {
          if (active) navigate("/login", { replace: true });
          return;
        }
      }

      // First fetchMe attempt with the current access token.
      try {
        const me = await fetchMe(token);
        if (!active) return;
        setWorkspace(me);
        setReady(true);
        return;
      } catch (_) {
        // Fall through to the single-shot refresh-and-retry recovery below.
      }
      if (!active) return;

      // 06.9-B: token present + fetchMe 401. If we already consumed the
      // persistent session in this mount, do NOT try again (prevents loops).
      if (refreshedThisMount) {
        clearToken();
        navigate("/login", { replace: true });
        return;
      }

      // Stale access token: discard it and try the existing persistent
      // session ONCE via the same refreshSession() used above.
      clearToken();
      let newToken = null;
      try {
        const data = await refreshSession();
        newToken = data?.access_token || null;
      } catch (_) {
        newToken = null;
      }
      if (!active) return;
      if (!newToken) {
        navigate("/login", { replace: true });
        return;
      }
      try {
        const me = await fetchMe(newToken);
        if (!active) return;
        setWorkspace(me);
        setReady(true);
      } catch (_) {
        if (!active) return;
        clearToken();
        navigate("/login", { replace: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50">
        <div data-testid={DASHBOARD.loading} className="flex items-center gap-3 text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Caricamento…
        </div>
      </div>
    );
  }

  const NavLinks = ({ onNavigate }) =>
    NAV.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.tid} className={linkClasses} onClick={onNavigate}>
        <n.icon className="h-4 w-4" />
        {n.label}
      </NavLink>
    ));

  return (
    <div data-testid={DASHBOARD.shell} className="min-h-screen bg-neutral-50 text-neutral-950 md:flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:border-r md:border-neutral-200 md:bg-white md:px-4 md:py-6">
        <div className="px-1">
          <MastroBrand className="h-9" />
        </div>
        <nav className="mt-8 flex flex-1 flex-col gap-1">
          <NavLinks />
        </nav>
        <button
          type="button"
          data-testid={DASHBOARD.logout}
          onClick={onLogout}
          className="mt-4 flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-950"
        >
          <LogOut className="h-4 w-4" />
          Esci
        </button>
      </aside>

      {/* Mobile top bar + nav */}
      <div className="md:hidden sticky top-0 z-10 border-b border-neutral-200 bg-white">
        <div className="flex items-center justify-between px-4 py-3">
          <MastroBrand className="h-8" />
          <button
            type="button"
            data-testid={`${DASHBOARD.logout}-mobile`}
            onClick={onLogout}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm font-medium text-neutral-700"
          >
            <LogOut className="h-4 w-4" />
            Esci
          </button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-2">
          <NavLinks />
        </nav>
      </div>

      {/* Content */}
      <main className="flex-1 px-5 py-6 md:px-10 md:py-10">
        <div className="mx-auto w-full max-w-3xl">
          <Outlet context={{ workspace }} />
          {/* MS-L4B — Shared legal footer for the whole authenticated area. */}
          <footer
            data-testid={DASHBOARD.legalFooter}
            className="mt-12 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t border-neutral-200 pt-4 text-xs text-neutral-400"
          >
            <Link
              to="/privacy"
              data-testid={DASHBOARD.legalPrivacyLink}
              className="text-neutral-500 hover:text-neutral-700"
            >
              Privacy Policy
            </Link>
            <span aria-hidden="true">·</span>
            <Link
              to="/termini"
              data-testid={DASHBOARD.legalTermsLink}
              className="text-neutral-500 hover:text-neutral-700"
            >
              Termini
            </Link>
          </footer>
        </div>
      </main>
    </div>
  );
}
